import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Typography,
} from "@mui/material";
import { ArrowBack, FiberManualRecord, Refresh } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { ar, enUS } from "date-fns/locale";
import HelmetMeta from "../../components/HelmetMeta";
import LogoutButton from "../../components/LogoutButton";
import LanguageToggle from "../../components/LanguageToggle/LanguageToggle";
import { AppContext } from "../../AppContext";
import { useLocalization } from "../../i18n/LocalizationProvider";
import { fToNow } from "../../utils/formatTime";
import { parseDateSafe } from "../KitchenDashboard/utils/orderTime";
import {
    getKdsStationCurrentItemsQueryKey,
    getKdsStationsQueryKey,
    useFinishKdsStationItems,
    useKdsItemLabels,
    useKdsSettings,
    useKdsStationCurrentItems,
    useKdsStations,
} from "../../shared/hooks/useKdsStations";

const parseServerMessages = (value) => {
    if (!value) return [];

    try {
        const parsed = typeof value === "string" ? JSON.parse(value) : value;
        if (!Array.isArray(parsed)) return [];

        return parsed
            .map((entry) => {
                if (!entry) return "";
                if (typeof entry === "string") {
                    try {
                        const nested = JSON.parse(entry);
                        return nested?.message || entry;
                    } catch {
                        return entry;
                    }
                }
                return entry?.message || "";
            })
            .filter(Boolean);
    } catch {
        return [];
    }
};

const getErrorMessage = (error, fallback) => {
    const responseData = error?.response?.data || {};
    const serverMessages = parseServerMessages(responseData?._server_messages);

    return (
        serverMessages[0] ||
        responseData?._error_message ||
        responseData?.exception ||
        error?.message ||
        fallback
    );
};

const itemLabelFromMap = (itemLabelsMap, code, fallback) => {
    if (!code) return fallback;
    return itemLabelsMap.get(code) || code;
};

const flattenStationGroups = (groups = []) =>
    (groups || []).filter(
        (group) => group && Array.isArray(group.items) && group.items.length > 0
    );

const getBillCountItemCode = (group, item) => {
    const directItemCode = String(item?.item || "").trim();
    if (directItemCode) return directItemCode;

    const parentItemCode = String(
        item?.parent_item_code || group?.parent_item_code || ""
    ).trim();
    if (parentItemCode) return parentItemCode;

    return "";
};

const getServiceChannel = (group, item) => {
    const itemChannel = String(item?.service_channel || "").trim();
    if (itemChannel) return itemChannel;

    const groupChannel = String(group?.service_channel || "").trim();
    if (groupChannel) return groupChannel;

    return "unknown";
};

const SERVICE_CHANNEL_ORDER = ["in_house", "delivery", "takeaway", "unknown"];

const KDS_COLORS = {
    shell: "#11191f",
    panel: "#162129",
    panelSoft: "#1b272f",
    card: "#2f3942",
    cardAlt: "#26313a",
    border: "rgba(255,255,255,0.06)",
    borderStrong: "rgba(255,106,61,0.28)",
    accent: "#ff6a3d",
    accentSoft: "rgba(255,106,61,0.14)",
    text: "#f4f7fb",
    muted: "#97a5b1",
    mutedSoft: "#73808c",
    success: "#ff9c84",
};

const formatSimilaritySignature = (signature, itemLabelsMap) => {
    const raw = String(signature || "").trim();
    if (!raw) return "";

    const groupedAddonsPrefix = "Grouped Addons:";
    if (!raw.startsWith(groupedAddonsPrefix)) {
        return raw;
    }

    const payload = raw.slice(groupedAddonsPrefix.length).trim();
    if (!payload) return groupedAddonsPrefix;

    try {
        const parsed = JSON.parse(payload);
        if (!Array.isArray(parsed)) return raw;

        const labels = parsed
            .map((code) => itemLabelFromMap(itemLabelsMap, code, code))
            .filter(Boolean);

        return `${groupedAddonsPrefix} ${labels.join(" + ")}`;
    } catch {
        return raw;
    }
};

const buildGroupTitle = (group, itemLabelsMap, t) => {
    if (group?.parent_item_name) return group.parent_item_name;
    if (group?.parent_item_code) {
        return itemLabelFromMap(itemLabelsMap, group.parent_item_code, t("kds.itemFallback"));
    }

    const firstItem = Array.isArray(group?.items) ? group.items[0] : null;
    if (firstItem?.item) {
        return itemLabelFromMap(itemLabelsMap, firstItem.item, t("kds.itemFallback"));
    }

    return group?.similarity_signature || t("kds.itemFallback");
};

const getGroupNotes = (items = []) => {
    return Array.from(
        new Set(
            items
                .map((item) => String(item?.notes || "").trim())
                .filter(Boolean)
        )
    ).slice(0, 3);
};

const getGroupedItemQtySummaries = (items = [], itemLabelsMap, t) => {
    const itemTotals = new Map();

    items.forEach((item) => {
        const itemCode = String(item?.item || "").trim();
        if (!itemCode) return;

        if (!itemTotals.has(itemCode)) {
            itemTotals.set(itemCode, {
                itemCode,
                itemLabel: itemLabelFromMap(itemLabelsMap, itemCode, t("kds.itemFallback")),
                totalQty: 0,
            });
        }

        itemTotals.get(itemCode).totalQty += Number(item?.qty || 0);
    });

    return Array.from(itemTotals.values()).sort((left, right) => {
        if (right.totalQty !== left.totalQty) return right.totalQty - left.totalQty;
        return left.itemLabel.localeCompare(right.itemLabel);
    });
};

function StationGroupCard({
    group,
    itemLabelsMap,
    t,
    distanceLocale,
    layout = "board",
}) {
    const items = Array.isArray(group?.items) ? group.items : [];
    const totalQty = items.reduce((sum, item) => sum + Number(item?.qty || 0), 0);
    const itemQtySummaries = getGroupedItemQtySummaries(items, itemLabelsMap, t);
    const formattedSignature = formatSimilaritySignature(
        group?.similarity_signature,
        itemLabelsMap
    );
    const createdAt = parseDateSafe(items[0]?.order_creation);
    const groupNotes = getGroupNotes(items);
    const tableOrder = items.find((item) => item?.table_order)?.table_order;
    const isLaneLayout = layout === "lane";
    const hasNotes = groupNotes.length > 0;

    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 3,
                backgroundColor: KDS_COLORS.card,
                borderColor: KDS_COLORS.border,
                boxShadow: isLaneLayout
                    ? `inset 0 1px 0 ${KDS_COLORS.borderStrong}, 0 12px 28px rgba(0,0,0,0.25)`
                    : "0 12px 28px rgba(0,0,0,0.18)",
                overflow: "hidden",
            }}
        >
            <CardContent
                sx={{
                    p: { xs: 2.5, md: 3 },
                    minHeight: isLaneLayout ? 220 : 200,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                }}
            >
                <Stack spacing={2.25} sx={{ height: "100%" }}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                    >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                                sx={{
                                    color: KDS_COLORS.accent,
                                    fontSize: { xs: "2.2rem", md: "2.7rem" },
                                    fontWeight: 900,
                                    lineHeight: 1,
                                    mb: 1,
                                }}
                            >
                                x{totalQty}
                            </Typography>
                            <Typography
                                variant={isLaneLayout ? "h4" : "h5"}
                                sx={{
                                    fontWeight: 900,
                                    lineHeight: 1.18,
                                    fontSize: isLaneLayout
                                        ? { xs: "1.65rem", md: "1.9rem" }
                                        : { xs: "1.45rem", md: "1.6rem" },
                                    color: KDS_COLORS.text,
                                }}
                            >
                                {buildGroupTitle(group, itemLabelsMap, t)}
                            </Typography>
                            {formattedSignature ? (
                                <Typography
                                    variant="body1"
                                    sx={{
                                        mt: 0.5,
                                        fontSize: { xs: "0.94rem", md: "0.98rem" },
                                        lineHeight: 1.45,
                                        overflowWrap: "anywhere",
                                        color: KDS_COLORS.muted,
                                    }}
                                >
                                    {t("kdsStations.groupedBy", {
                                        value: formattedSignature,
                                    })}
                                </Typography>
                            ) : null}
                        </Box>
                        <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                            justifyContent="flex-end"
                        >
                            {tableOrder ? (
                                <Chip
                                    label={t("kdsStations.tableOrderChip", {
                                        value: tableOrder,
                                    })}
                                    sx={{
                                        height: 34,
                                        backgroundColor: KDS_COLORS.panel,
                                        color: KDS_COLORS.text,
                                        border: `1px solid ${KDS_COLORS.border}`,
                                        "& .MuiChip-label": {
                                            px: 1.2,
                                            fontSize: "0.86rem",
                                            fontWeight: 700,
                                        },
                                    }}
                                />
                            ) : null}
                            {createdAt ? (
                                <Chip
                                    label={t("kdsStations.createdAgo", {
                                        time: fToNow(createdAt, distanceLocale),
                                    })}
                                    sx={{
                                        height: 34,
                                        backgroundColor: KDS_COLORS.accentSoft,
                                        color: KDS_COLORS.success,
                                        "& .MuiChip-label": {
                                            px: 1.2,
                                            fontSize: "0.86rem",
                                            fontWeight: 800,
                                        },
                                    }}
                                />
                            ) : null}
                        </Stack>
                    </Stack>

                    <Divider sx={{ borderColor: KDS_COLORS.border }} />

                    <Stack spacing={1.25} sx={{ mt: "auto" }}>
                        {itemQtySummaries.map((summary) => (
                            <Box
                                key={summary.itemCode}
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 1.25,
                                    px: 1.25,
                                    py: 0.95,
                                    borderRadius: 1.5,
                                    backgroundColor: KDS_COLORS.cardAlt,
                                    border: `1px solid ${KDS_COLORS.border}`,
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: KDS_COLORS.text,
                                        fontWeight: 800,
                                        fontSize: isLaneLayout
                                            ? { xs: "0.98rem", md: "1.04rem" }
                                            : { xs: "0.94rem", md: "1rem" },
                                        lineHeight: 1.35,
                                    }}
                                >
                                    {summary.itemLabel}
                                </Typography>
                                <Typography
                                    sx={{
                                        color: KDS_COLORS.accent,
                                        fontWeight: 900,
                                        fontSize: isLaneLayout
                                            ? { xs: "1.02rem", md: "1.08rem" }
                                            : { xs: "0.98rem", md: "1.02rem" },
                                        lineHeight: 1,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    x{summary.totalQty}
                                </Typography>
                            </Box>
                        ))}

                        {hasNotes ? (
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {groupNotes.map((note) => (
                                    <Box
                                        key={note}
                                        sx={{
                                            px: 1.4,
                                            py: 0.8,
                                            borderRadius: 1.75,
                                            backgroundColor: KDS_COLORS.cardAlt,
                                            border: `1px solid ${KDS_COLORS.border}`,
                                            color: KDS_COLORS.success,
                                            fontSize: { xs: "0.92rem", md: "0.96rem" },
                                            fontWeight: 800,
                                            lineHeight: 1.45,
                                            letterSpacing: "0.02em",
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        {note}
                                    </Box>
                                ))}
                            </Stack>
                        ) : null}

                        {(items[0]?.parent_item_name || items[0]?.parent_item_code) && !hasNotes ? (
                            <Box
                                sx={{
                                    px: 1.4,
                                    py: 0.85,
                                    width: "fit-content",
                                    borderRadius: 1.75,
                                    backgroundColor: KDS_COLORS.cardAlt,
                                    border: `1px solid ${KDS_COLORS.border}`,
                                    color: KDS_COLORS.muted,
                                    fontSize: { xs: "0.88rem", md: "0.92rem" },
                                    fontWeight: 700,
                                }}
                            >
                                {t("kdsStations.parentItemChip", {
                                    value:
                                        items[0]?.parent_item_name ||
                                        itemLabelFromMap(
                                            itemLabelsMap,
                                            items[0]?.parent_item_code,
                                            items[0]?.parent_item_code
                                        ),
                                })}
                            </Box>
                        ) : null}

                        <Box
                            sx={{
                                mt: 0.5,
                                height: 6,
                                width: "100%",
                                borderRadius: 999,
                                backgroundColor: "rgba(255,255,255,0.06)",
                                overflow: "hidden",
                            }}
                        >
                            <Box
                                sx={{
                                    width: `${Math.min(100, Math.max(28, totalQty * 12))}%`,
                                    height: "100%",
                                    borderRadius: 999,
                                    backgroundColor: KDS_COLORS.success,
                                }}
                            />
                        </Box>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}

function KdsStationsPage() {
    const { t } = useTranslation();
    const { language } = useLocalization();
    const navigate = useNavigate();
    const { stationName: routeStationName, substation: routeSubstation } =
        useParams();
    const queryClient = useQueryClient();
    const distanceLocale = language === "ar" ? ar : enUS;
    const { socket } = useContext(AppContext);

    const [activeFinishKey, setActiveFinishKey] = useState(null);
    const [isSocketConnected, setIsSocketConnected] = useState(
        !!socket?.connected
    );
    const realtimeRefreshTimeoutRef = useRef(null);

    const { settings, isLoading: isSettingsLoading, error: settingsError, refetch: refetchSettings } =
        useKdsSettings();
    const {
        data: stations,
        isLoading: isStationsLoading,
        error: stationsError,
        refetch: refetchStations,
    } = useKdsStations();

    const stationList = useMemo(() => (Array.isArray(stations) ? stations : []), [stations]);

    const resolvedStationName = useMemo(() => {
        if (!routeStationName) return "";
        return String(routeStationName || "").trim();
    }, [routeStationName]);

    const resolvedRouteSubstation = useMemo(() => {
        if (!routeSubstation && routeSubstation !== "0") return null;
        const parsed = Number(routeSubstation);
        if (!Number.isInteger(parsed) || parsed <= 0) return null;
        return parsed;
    }, [routeSubstation]);

    const selectedStation = useMemo(() => {
        if (!resolvedStationName) return null;
        return (
            stationList.find(
                (station) =>
                    String(station?.name || "").trim() === resolvedStationName
                ) || null
        );
    }, [resolvedStationName, stationList]);

    useEffect(() => {
        if (isStationsLoading || stationList.length === 0) return;

        if (!resolvedStationName) {
            navigate(`/kds-stations/${encodeURIComponent(stationList[0].name)}`, {
                replace: true,
            });
            return;
        }

        if (!selectedStation) {
            navigate(`/kds-stations/${encodeURIComponent(stationList[0].name)}`, {
                replace: true,
            });
        }
    }, [
        isStationsLoading,
        navigate,
        resolvedStationName,
        selectedStation,
        stationList,
    ]);

    const {
        data: currentItemsData,
        isLoading: isCurrentItemsLoading,
        error: currentItemsError,
        refetch: refetchCurrentItems,
        isFetching: isCurrentItemsFetching,
    } = useKdsStationCurrentItems(selectedStation?.name, {
        enabled: !!selectedStation?.name,
        refetchInterval: 5000,
    });

    const currentItems = useMemo(
        () => (Array.isArray(currentItemsData) ? currentItemsData : []),
        [currentItemsData]
    );

    const finishMutation = useFinishKdsStationItems();

    useEffect(() => {
        if (!socket) {
            setIsSocketConnected(false);
            return undefined;
        }

        const handleConnect = () => setIsSocketConnected(true);
        const handleDisconnect = () => setIsSocketConnected(false);

        setIsSocketConnected(!!socket.connected);
        socket.on("connect", handleConnect);
        socket.on("disconnect", handleDisconnect);
        socket.on("connect_error", handleDisconnect);

        return () => {
            if (socket?.off) {
                socket.off("connect", handleConnect);
                socket.off("disconnect", handleDisconnect);
                socket.off("connect_error", handleDisconnect);
            } else if (socket?.removeListener) {
                socket.removeListener("connect", handleConnect);
                socket.removeListener("disconnect", handleDisconnect);
                socket.removeListener("connect_error", handleDisconnect);
            }
        };
    }, [socket]);

    const scheduleRealtimeRefresh = useCallback(() => {
        if (!selectedStation?.name) return;

        if (realtimeRefreshTimeoutRef.current) {
            clearTimeout(realtimeRefreshTimeoutRef.current);
        }

        realtimeRefreshTimeoutRef.current = setTimeout(() => {
            queryClient.invalidateQueries(
                getKdsStationCurrentItemsQueryKey(selectedStation.name)
            );
            queryClient.invalidateQueries(getKdsStationsQueryKey());
            refetchCurrentItems?.();
            realtimeRefreshTimeoutRef.current = null;
        }, 200);
    }, [queryClient, refetchCurrentItems, selectedStation?.name]);

    useEffect(() => {
        if (!socket || !selectedStation?.name) return undefined;

        const allowedDoctypes = new Set(["KDS Order", "Table Order", "KDS Station"]);
        socket.emit("doctype_subscribe", "KDS Order");
        socket.emit("doctype_subscribe", "Table Order");
        socket.emit("doctype_subscribe", "KDS Station");

        const handleListUpdate = (data) => {
            const doctype = String(data?.doctype || "").trim();
            if (!doctype || !allowedDoctypes.has(doctype)) return;
            scheduleRealtimeRefresh();
        };

        const handleOrderSync = () => {
            scheduleRealtimeRefresh();
        };

        const handleOrderVoided = () => {
            scheduleRealtimeRefresh();
        };

        const handleEntryDeleted = () => {
            scheduleRealtimeRefresh();
        };

        const handleKdsStationUpdated = (message) => {
            const stations = Array.isArray(message?.stations) ? message.stations : [];
            if (
                stations.length === 0 ||
                stations.includes(selectedStation.name)
            ) {
                scheduleRealtimeRefresh();
            }
        };

        socket.on("list_update", handleListUpdate);
        socket.on("synchronize_order_data", handleOrderSync);
        socket.on("table_order_voided", handleOrderVoided);
        socket.on("entry_item_deleted", handleEntryDeleted);
        socket.on("kds_station_updated", handleKdsStationUpdated);

        return () => {
            if (realtimeRefreshTimeoutRef.current) {
                clearTimeout(realtimeRefreshTimeoutRef.current);
                realtimeRefreshTimeoutRef.current = null;
            }

            if (socket?.off) {
                socket.off("list_update", handleListUpdate);
                socket.off("synchronize_order_data", handleOrderSync);
                socket.off("table_order_voided", handleOrderVoided);
                socket.off("entry_item_deleted", handleEntryDeleted);
                socket.off("kds_station_updated", handleKdsStationUpdated);
            } else if (socket?.removeListener) {
                socket.removeListener("list_update", handleListUpdate);
                socket.removeListener("synchronize_order_data", handleOrderSync);
                socket.removeListener("table_order_voided", handleOrderVoided);
                socket.removeListener("entry_item_deleted", handleEntryDeleted);
                socket.removeListener(
                    "kds_station_updated",
                    handleKdsStationUpdated
                );
            }
        };
    }, [scheduleRealtimeRefresh, selectedStation?.name, socket]);

    const normalizedSubstationSections = useMemo(() => {
        const configuredSubstations = Number(selectedStation?.substations || 0);
        if (!selectedStation || configuredSubstations <= 0) return [];

        const existingSections = new Map();
        currentItems.forEach((entry) => {
            if (!entry || !Object.prototype.hasOwnProperty.call(entry, "groups")) return;
            const key = Number(entry?.substation ?? 0);
            existingSections.set(key, Array.isArray(entry?.groups) ? entry.groups : []);
        });

        const keys = [];
        if (existingSections.has(0)) keys.push(0);
        for (let i = 1; i <= configuredSubstations; i += 1) {
            keys.push(i);
        }
        Array.from(existingSections.keys())
            .filter((key) => key > configuredSubstations && key !== 0)
            .sort((a, b) => a - b)
            .forEach((key) => keys.push(key));

        return keys.map((key) => ({
            substation: key,
            groups: existingSections.get(key) || [],
        }));
    }, [currentItems, selectedStation]);

    useEffect(() => {
        if (!selectedStation) return;
        if (routeSubstation === undefined) return;

        const configuredSubstations = Number(selectedStation?.substations || 0);
        const hasValidSubstation =
            resolvedRouteSubstation !== null &&
            configuredSubstations > 0 &&
            resolvedRouteSubstation <= configuredSubstations;

        if (!hasValidSubstation) {
            navigate(`/kds-stations/${encodeURIComponent(selectedStation.name)}`, {
                replace: true,
            });
        }
    }, [
        navigate,
        resolvedRouteSubstation,
        routeSubstation,
        selectedStation,
    ]);

    const normalizedGroups = useMemo(() => {
        if (Number(selectedStation?.substations || 0) > 0) return [];
        return currentItems.filter(
            (entry) => !Object.prototype.hasOwnProperty.call(entry || {}, "groups")
        );
    }, [currentItems, selectedStation]);

    const displayedSubstationSections = useMemo(() => {
        if (resolvedRouteSubstation === null) return normalizedSubstationSections;
        return normalizedSubstationSections.filter(
            (section) => Number(section?.substation) === resolvedRouteSubstation
        );
    }, [normalizedSubstationSections, resolvedRouteSubstation]);

    const displayedGroups = useMemo(() => {
        if (Number(selectedStation?.substations || 0) > 0) {
            return displayedSubstationSections.flatMap((section) =>
                flattenStationGroups(section?.groups)
            );
        }

        return flattenStationGroups(normalizedGroups);
    }, [displayedSubstationSections, normalizedGroups, selectedStation]);

    const itemCodes = useMemo(() => {
        const codes = new Set();
        const collectGroups = (groups) => {
            (groups || []).forEach((group) => {
                if (group?.parent_item_code) codes.add(group.parent_item_code);
                (group?.items || []).forEach((item) => {
                    if (item?.item) codes.add(item.item);
                    if (item?.parent_item_code) codes.add(item.parent_item_code);
                });
            });
        };

        collectGroups(normalizedGroups);
        normalizedSubstationSections.forEach((section) => collectGroups(section?.groups));
        return Array.from(codes);
    }, [normalizedGroups, normalizedSubstationSections]);

    const { data: itemLabelsData } = useKdsItemLabels(itemCodes);

    const itemLabelsMap = useMemo(() => {
        const map = new Map();
        (itemLabelsData || []).forEach((item) => {
            if (!item?.name) return;
            map.set(item.name, item.item_name || item.name);
        });
        return map;
    }, [itemLabelsData]);

    const billCountSections = useMemo(() => {
        const countsByChannel = new Map();

        displayedGroups.forEach((group) => {
            (group?.items || []).forEach((item) => {
                const itemCode = getBillCountItemCode(group, item);
                if (!itemCode) return;
                const serviceChannel = getServiceChannel(group, item);
                const itemQty = Number(item?.qty || 0);
                if (!itemQty) return;

                if (!countsByChannel.has(serviceChannel)) {
                    countsByChannel.set(serviceChannel, new Map());
                }

                const countsByItemCode = countsByChannel.get(serviceChannel);
                if (!countsByItemCode.has(itemCode)) {
                    countsByItemCode.set(itemCode, {
                        itemCode,
                        itemLabel: itemLabelFromMap(itemLabelsMap, itemCode, itemCode),
                        totalQty: 0,
                    });
                }

                countsByItemCode.get(itemCode).totalQty += itemQty;
            });
        });

        return SERVICE_CHANNEL_ORDER.map((serviceChannel) => {
            const countsByItemCode = countsByChannel.get(serviceChannel);
            if (!countsByItemCode || countsByItemCode.size === 0) return null;

            return {
                serviceChannel,
                title: t(`kdsStations.serviceChannels.${serviceChannel}`),
                summaries: Array.from(countsByItemCode.values())
                    .map((entry) => ({
                        itemCode: entry.itemCode,
                        itemLabel: entry.itemLabel,
                        totalQty: entry.totalQty,
                    }))
                    .sort((left, right) => {
                        if (right.totalQty !== left.totalQty) {
                            return right.totalQty - left.totalQty;
                        }
                        return left.itemLabel.localeCompare(right.itemLabel);
                    }),
            };
        }).filter(Boolean);
    }, [displayedGroups, itemLabelsMap, t]);

    const handleSelectStation = useCallback(
        (event) => {
            const nextStationName = event.target.value;
            if (!nextStationName) return;
            navigate(`/kds-stations/${encodeURIComponent(nextStationName)}`);
        },
        [navigate]
    );

    const handleOpenSubstation = useCallback(
        (substation) => {
            if (!selectedStation?.name || !substation) return;
            navigate(
                `/kds-stations/${encodeURIComponent(
                    selectedStation.name
                )}/${encodeURIComponent(String(substation))}`
            );
        },
        [navigate, selectedStation?.name]
    );

    const handleBackToStation = useCallback(() => {
        if (!selectedStation?.name) return;
        navigate(`/kds-stations/${encodeURIComponent(selectedStation.name)}`);
    }, [navigate, selectedStation?.name]);

    const handleRefresh = useCallback(() => {
        refetchSettings?.();
        refetchStations?.();
        refetchCurrentItems?.();
    }, [refetchCurrentItems, refetchSettings, refetchStations]);

    const handleFinish = useCallback(
        async (substation) => {
            if (!selectedStation?.name) return;

            const finishKey =
                substation === undefined || substation === null
                    ? `${selectedStation.name}:all`
                    : `${selectedStation.name}:${substation}`;

            setActiveFinishKey(finishKey);
            try {
                await finishMutation.mutateAsync({
                    stationName: selectedStation.name,
                    substation,
                });
                await refetchCurrentItems?.();
            } catch (error) {
                enqueueSnackbar(
                    getErrorMessage(error, t("kdsStations.finishError")),
                    {
                        variant: "error",
                    }
                );
            } finally {
                setActiveFinishKey(null);
            }
        },
        [finishMutation, refetchCurrentItems, selectedStation, t]
    );

    const isLoading =
        isSettingsLoading || isStationsLoading || (!!selectedStation && isCurrentItemsLoading);
    const error = settingsError || stationsError || currentItemsError;
    const kdsEnabled = Number(settings?.enable_kds) === 1;
    const isStationFetching = isCurrentItemsFetching || finishMutation.isLoading;
    const isSubstationRouteActive = resolvedRouteSubstation !== null;
    const liveSyncLabel = isSocketConnected
        ? t("kdsStations.liveSync")
        : t("kdsStations.pollingFallback");

    return (
        <>
            <HelmetMeta title={t("kdsStations.helmetTitle")} />

            <Box
                sx={{
                    p: { xs: 1.5, md: 2.5 },
                    minHeight: "100vh",
                    background: `linear-gradient(180deg, ${KDS_COLORS.shell} 0%, #0f171d 100%)`,
                }}
            >
                <Stack
                    spacing={2.5}
                    sx={{
                        minHeight: "calc(100vh - 32px)",
                        p: { xs: 1.5, md: 2 },
                        borderRadius: 3,
                        border: `1px solid ${KDS_COLORS.border}`,
                        backgroundColor: "#10191f",
                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
                    }}
                >
                    <Card
                        sx={{
                            borderRadius: 3,
                            backgroundColor: KDS_COLORS.panel,
                            border: `1px solid ${KDS_COLORS.border}`,
                            boxShadow: "none",
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                            <Stack spacing={2.5}>
                                <Stack
                                    direction={{ xs: "column", md: "row" }}
                                    spacing={2}
                                    justifyContent="space-between"
                                    alignItems={{ xs: "stretch", md: "center" }}
                                >
                                    <Box>
                                        <Typography
                                            variant="h3"
                                            sx={{
                                                fontWeight: 900,
                                                fontSize: { xs: "2rem", md: "2.5rem" },
                                                lineHeight: 1.1,
                                                color: KDS_COLORS.accent,
                                                textTransform: "uppercase",
                                                letterSpacing: "0.02em",
                                            }}
                                        >
                                            {selectedStation?.station_name ||
                                                t("kdsStations.dashboardTitle")}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                mt: 0.75,
                                                fontSize: { xs: "1rem", md: "1.05rem" },
                                                color: KDS_COLORS.muted,
                                                textTransform: "uppercase",
                                                letterSpacing: "0.08em",
                                            }}
                                        >
                                            {selectedStation?.station_type
                                                ? t("kdsStations.stationTypeLabel", {
                                                      value: selectedStation.station_type,
                                                  })
                                                : t("kdsStations.stationDashboardHint")}
                                        </Typography>
                                        {isSubstationRouteActive ? (
                                            <Typography
                                                variant="body1"
                                                color="text.secondary"
                                                sx={{
                                                    mt: 0.5,
                                                    fontSize: {
                                                        xs: "1rem",
                                                        md: "1.05rem",
                                                    },
                                                color: KDS_COLORS.muted,
                                                }}
                                            >
                                                {t("kdsStations.substationRouteHint", {
                                                    count: resolvedRouteSubstation,
                                                })}
                                            </Typography>
                                        ) : null}
                                    </Box>

                                    <Stack
                                        direction={{ xs: "column", sm: "row" }}
                                        spacing={1.25}
                                        alignItems={{ xs: "stretch", sm: "center" }}
                                    >
                                        <Box
                                            sx={{
                                                px: 1.5,
                                                minHeight: 44,
                                                borderRadius: 999,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                backgroundColor: KDS_COLORS.panelSoft,
                                                border: `1px solid ${KDS_COLORS.border}`,
                                                color: KDS_COLORS.text,
                                                fontSize: "0.82rem",
                                                fontWeight: 800,
                                                textTransform: "uppercase",
                                                letterSpacing: "0.08em",
                                            }}
                                        >
                                            <FiberManualRecord
                                                sx={{
                                                    fontSize: 12,
                                                    color: isSocketConnected
                                                        ? KDS_COLORS.success
                                                        : KDS_COLORS.accent,
                                                }}
                                            />
                                            {liveSyncLabel}
                                        </Box>
                                        <FormControl sx={{ minWidth: 280 }}>
                                            <InputLabel id="kds-station-select-label">
                                                {t("kdsStations.station")}
                                            </InputLabel>
                                            <Select
                                                labelId="kds-station-select-label"
                                                value={selectedStation?.name || ""}
                                                label={t("kdsStations.station")}
                                                onChange={handleSelectStation}
                                                disabled={!stationList.length}
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: {
                                                            mt: 1,
                                                            borderRadius: 2,
                                                            backgroundColor:
                                                                KDS_COLORS.panelSoft,
                                                            color: KDS_COLORS.text,
                                                            border: `1px solid ${KDS_COLORS.border}`,
                                                            boxShadow:
                                                                "0 18px 38px rgba(0,0,0,0.35)",
                                                            "& .MuiMenuItem-root": {
                                                                color: KDS_COLORS.text,
                                                                backgroundColor:
                                                                    KDS_COLORS.panelSoft,
                                                            },
                                                            "& .MuiMenuItem-root:hover": {
                                                                backgroundColor:
                                                                    KDS_COLORS.accentSoft,
                                                            },
                                                            "& .MuiMenuItem-root.Mui-focusVisible": {
                                                                backgroundColor:
                                                                    KDS_COLORS.accentSoft,
                                                                color: KDS_COLORS.text,
                                                            },
                                                            "& .MuiMenuItem-root.Mui-selected": {
                                                                backgroundColor:
                                                                    KDS_COLORS.accentSoft,
                                                                color: KDS_COLORS.text,
                                                                fontWeight: 800,
                                                            },
                                                            "& .MuiMenuItem-root.Mui-selected:hover":
                                                                {
                                                                    backgroundColor:
                                                                        "rgba(255,106,61,0.22)",
                                                                },
                                                        },
                                                    },
                                                }}
                                                sx={{
                                                    minHeight: 60,
                                                    fontSize: "1.05rem",
                                                    backgroundColor: KDS_COLORS.panelSoft,
                                                    color: KDS_COLORS.text,
                                                    ".MuiOutlinedInput-notchedOutline": {
                                                        borderColor: KDS_COLORS.border,
                                                    },
                                                    "& .MuiSvgIcon-root": {
                                                        color: KDS_COLORS.text,
                                                    },
                                                }}
                                            >
                                                {stationList.map((station) => (
                                                    <MenuItem
                                                        key={station.name}
                                                        value={station.name}
                                                        sx={{
                                                            fontSize: "1rem",
                                                            backgroundColor: KDS_COLORS.panelSoft,
                                                            color: KDS_COLORS.text,
                                                            "&.Mui-selected": {
                                                                backgroundColor:
                                                                    KDS_COLORS.accentSoft,
                                                                color: KDS_COLORS.text,
                                                                fontWeight: 800,
                                                            },
                                                            "&.Mui-selected:hover": {
                                                                backgroundColor:
                                                                    "rgba(255,106,61,0.22)",
                                                            },
                                                            "&.Mui-focusVisible": {
                                                                backgroundColor:
                                                                    KDS_COLORS.accentSoft,
                                                                color: KDS_COLORS.text,
                                                            },
                                                        }}
                                                    >
                                                        {station.station_name || station.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <LanguageToggle />
                                        <LogoutButton />
                                        <IconButton
                                            onClick={handleRefresh}
                                            disabled={isStationFetching}
                                            sx={{
                                                width: 56,
                                                height: 56,
                                                border: "1px solid",
                                                borderColor: KDS_COLORS.border,
                                                color: KDS_COLORS.text,
                                                backgroundColor: KDS_COLORS.panelSoft,
                                            }}
                                        >
                                            <Refresh sx={{ fontSize: 28 }} />
                                        </IconButton>
                                    </Stack>
                                </Stack>

                                {selectedStation ? (
                                    <Stack spacing={1.25}>
                                        {isSubstationRouteActive ? (
                                            <Box>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<ArrowBack />}
                                                    onClick={handleBackToStation}
                                                    sx={{
                                                        minHeight: 48,
                                                        px: 2.25,
                                                        fontSize: "0.98rem",
                                                        fontWeight: 700,
                                                        color: KDS_COLORS.text,
                                                        borderColor: KDS_COLORS.border,
                                                        backgroundColor: KDS_COLORS.panelSoft,
                                                    }}
                                                >
                                                    {t("kdsStations.backToStationOverview")}
                                                </Button>
                                            </Box>
                                        ) : null}
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            flexWrap="wrap"
                                            useFlexGap
                                        >
                                            <Chip
                                                label={t("kdsStations.stationChip", {
                                                    value:
                                                        selectedStation.station_name ||
                                                        selectedStation.name,
                                                })}
                                                sx={{
                                                    height: 38,
                                                    backgroundColor: KDS_COLORS.panelSoft,
                                                    color: KDS_COLORS.text,
                                                    border: `1px solid ${KDS_COLORS.border}`,
                                                    "& .MuiChip-label": {
                                                        px: 1.25,
                                                        fontSize: "0.98rem",
                                                        fontWeight: 700,
                                                    },
                                                }}
                                            />
                                            {selectedStation.station_type ? (
                                            <Chip
                                                label={t("kdsStations.stationTypeChip", {
                                                    value: selectedStation.station_type,
                                                })}
                                                sx={{
                                                    height: 38,
                                                    backgroundColor: KDS_COLORS.panelSoft,
                                                    color: KDS_COLORS.text,
                                                    border: `1px solid ${KDS_COLORS.border}`,
                                                    "& .MuiChip-label": {
                                                        px: 1.25,
                                                        fontSize: "0.98rem",
                                                        fontWeight: 700,
                                                    },
                                                }}
                                            />
                                            ) : null}
                                            <Chip
                                                label={t("kdsStations.capacityChip", {
                                                    value: selectedStation.capacity || 0,
                                                })}
                                                sx={{
                                                    height: 38,
                                                    backgroundColor: KDS_COLORS.panelSoft,
                                                    color: KDS_COLORS.text,
                                                    border: `1px solid ${KDS_COLORS.border}`,
                                                    "& .MuiChip-label": {
                                                        px: 1.25,
                                                        fontSize: "0.98rem",
                                                        fontWeight: 700,
                                                    },
                                                }}
                                            />
                                            {selectedStation.line ? (
                                                <Chip
                                                    label={t("kdsStations.lineChip", {
                                                        value: selectedStation.line,
                                                    })}
                                                    sx={{
                                                        height: 38,
                                                        backgroundColor: KDS_COLORS.panelSoft,
                                                        color: KDS_COLORS.text,
                                                        border: `1px solid ${KDS_COLORS.border}`,
                                                        "& .MuiChip-label": {
                                                            px: 1.25,
                                                            fontSize: "0.98rem",
                                                            fontWeight: 700,
                                                        },
                                                    }}
                                                />
                                            ) : null}
                                            {Number(selectedStation.substations || 0) > 0 ? (
                                            <Chip
                                                color="secondary"
                                                label={t("kdsStations.substationsChip", {
                                                    count: Number(
                                                        selectedStation.substations || 0
                                                    ),
                                                })}
                                                sx={{
                                                    height: 38,
                                                    backgroundColor: KDS_COLORS.accentSoft,
                                                    color: KDS_COLORS.success,
                                                    "& .MuiChip-label": {
                                                        px: 1.25,
                                                        fontSize: "0.98rem",
                                                        fontWeight: 800,
                                                    },
                                                }}
                                            />
                                            ) : null}
                                        </Stack>
                                    </Stack>
                                ) : null}
                            </Stack>
                        </CardContent>
                    </Card>

                    {isLoading ? (
                        <Card
                            sx={{
                                borderRadius: 3,
                                backgroundColor: KDS_COLORS.panel,
                                border: `1px solid ${KDS_COLORS.border}`,
                                boxShadow: "none",
                            }}
                        >
                            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                <Stack
                                    spacing={2}
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{ minHeight: 280 }}
                                >
                                    <CircularProgress size={48} />
                                    <Typography
                                        sx={{ fontSize: { xs: "1.05rem", md: "1.1rem" } }}
                                        color={KDS_COLORS.muted}
                                    >
                                        {t("kdsStations.loading")}
                                    </Typography>
                                </Stack>
                            </CardContent>
                        </Card>
                    ) : null}

                    {!isLoading && error ? (
                        <Alert severity="error" sx={{ borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 900 }}>
                                {t("kdsStations.loadErrorTitle")}
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5 }}>
                                {getErrorMessage(error, t("kdsStations.loadErrorFallback"))}
                            </Typography>
                        </Alert>
                    ) : null}

                    {!isLoading && !error && !kdsEnabled ? (
                        <Alert severity="warning" sx={{ borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 900 }}>
                                {t("kdsStations.disabledTitle")}
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5 }}>
                                {t("kdsStations.disabledDescription")}
                            </Typography>
                        </Alert>
                    ) : null}

                    {!isLoading && !error && kdsEnabled && stationList.length === 0 ? (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 900 }}>
                                {t("kdsStations.noStationsTitle")}
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5 }}>
                                {t("kdsStations.noStationsDescription")}
                            </Typography>
                        </Alert>
                    ) : null}

                    {!isLoading &&
                    !error &&
                    kdsEnabled &&
                    selectedStation &&
                    billCountSections.length > 0 ? (
                        <Stack spacing={2.5}>
                            {billCountSections.map((section) => (
                                <Box key={section.serviceChannel}>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            mb: 1.5,
                                            fontWeight: 900,
                                            textTransform: "uppercase",
                                            color: KDS_COLORS.text,
                                        }}
                                    >
                                        {section.title}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 2,
                                            overflowX: "auto",
                                            pb: 0.5,
                                        }}
                                    >
                                        {section.summaries.map((summary) => {
                                            const hasQty = summary.totalQty > 0;
                                            return (
                                                <Stack
                                                    key={`${section.serviceChannel}-${summary.itemCode}`}
                                                    spacing={1}
                                                    alignItems="center"
                                                    sx={{ minWidth: 92, flexShrink: 0 }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 82,
                                                            height: 82,
                                                            borderRadius: "50%",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            border: `4px solid ${
                                                                hasQty
                                                                    ? "#27a7ff"
                                                                    : "#ff4d4f"
                                                            }`,
                                                            backgroundColor: KDS_COLORS.cardAlt,
                                                            color: KDS_COLORS.text,
                                                            fontSize: "2rem",
                                                            fontWeight: 900,
                                                            lineHeight: 1,
                                                            boxShadow:
                                                                "inset 0 1px 0 rgba(255,255,255,0.04)",
                                                        }}
                                                    >
                                                        {summary.totalQty}
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            px: 1.25,
                                                            py: 0.55,
                                                            borderRadius: 1.25,
                                                            backgroundColor: hasQty
                                                                ? "#27a7ff"
                                                                : "#ff4d4f",
                                                            color: "#10191f",
                                                            fontSize: "0.76rem",
                                                            fontWeight: 900,
                                                            lineHeight: 1,
                                                            letterSpacing: "0.04em",
                                                            textTransform: "uppercase",
                                                            maxWidth: 120,
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                        title={`${summary.itemLabel} (${summary.itemCode})`}
                                                    >
                                                        {summary.itemLabel}
                                                    </Box>
                                                </Stack>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    ) : null}

                    {!isLoading &&
                    !error &&
                    kdsEnabled &&
                    selectedStation &&
                    Number(selectedStation.substations || 0) <= 0 ? (
                        <Stack spacing={2.25}>
                            <Card
                                sx={{
                                    borderRadius: 3,
                                    backgroundColor: KDS_COLORS.panel,
                                    border: `1px solid ${KDS_COLORS.border}`,
                                    boxShadow: "none",
                                }}
                            >
                                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                                    <Stack
                                        direction={{ xs: "column", sm: "row" }}
                                        spacing={1.5}
                                        justifyContent="space-between"
                                        alignItems={{ xs: "flex-start", sm: "center" }}
                                    >
                                        <Box>
                                            <Typography
                                                variant="h5"
                                                sx={{ fontWeight: 900, lineHeight: 1.15 }}
                                            >
                                                {t("kdsStations.currentItemsTitle")}
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                color="text.secondary"
                                                sx={{
                                                    mt: 0.5,
                                                    fontSize: { xs: "1rem", md: "1.05rem" },
                                                color: KDS_COLORS.muted,
                                                }}
                                            >
                                                {t("kdsStations.currentItemsHint")}
                                            </Typography>
                                        </Box>
                                        <Button
                                            variant="contained"
                                            onClick={() => handleFinish(undefined)}
                                            disabled={
                                                finishMutation.isLoading ||
                                                normalizedGroups.length === 0
                                            }
                                            sx={{
                                                minHeight: 60,
                                                px: 3,
                                                fontSize: "1.05rem",
                                                fontWeight: 800,
                                                backgroundColor: KDS_COLORS.accent,
                                            }}
                                        >
                                            {activeFinishKey ===
                                            `${selectedStation.name}:all`
                                                ? t("kdsStations.finishing")
                                                : t("kdsStations.finishCurrentItems")}
                                        </Button>
                                    </Stack>
                                </CardContent>
                            </Card>

                            {normalizedGroups.length > 0 ? (
                                normalizedGroups.map((group, index) => (
                                    <StationGroupCard
                                        key={`${group?.similarity_signature || "group"}-${index}`}
                                        group={group}
                                        itemLabelsMap={itemLabelsMap}
                                        t={t}
                                        distanceLocale={distanceLocale}
                                    />
                                ))
                            ) : (
                                <Alert severity="info" sx={{ borderRadius: 2 }}>
                                    {t("kdsStations.noCurrentItems")}
                                </Alert>
                            )}
                        </Stack>
                    ) : null}

                    {!isLoading &&
                    !error &&
                    kdsEnabled &&
                    selectedStation &&
                    Number(selectedStation.substations || 0) > 0 ? (
                        <Box
                            sx={{
                                display: "grid",
                                gap: 2,
                                gridTemplateColumns: isSubstationRouteActive
                                    ? "minmax(0, 1fr)"
                                    : {
                                          xs: "minmax(0, 1fr)",
                                          xl: `repeat(${Math.max(
                                              1,
                                              displayedSubstationSections.length
                                          )}, minmax(280px, 1fr))`,
                                      },
                            }}
                        >
                            {displayedSubstationSections.map((section) => {
                                const sectionKey = `${selectedStation.name}:${section.substation}`;
                                const label =
                                    Number(section.substation) === 0
                                        ? t("kdsStations.unassignedSubstation")
                                        : t("kdsStations.substationLabel", {
                                              count: section.substation,
                                          });

                                return (
                                    <Card
                                        key={sectionKey}
                                        sx={{
                                            borderRadius: 3,
                                            backgroundColor: KDS_COLORS.panel,
                                            border: `1px solid ${KDS_COLORS.border}`,
                                            boxShadow: "none",
                                            minHeight: isSubstationRouteActive
                                                ? "calc(100vh - 260px)"
                                                : 560,
                                        }}
                                    >
                                        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                                            <Stack spacing={2} sx={{ height: "100%" }}>
                                                <Stack
                                                    direction={{
                                                        xs: "column",
                                                        sm: "row",
                                                    }}
                                                    spacing={1.5}
                                                    justifyContent="space-between"
                                                    alignItems={{
                                                        xs: "flex-start",
                                                        sm: "center",
                                                    }}
                                                >
                                                    <Box>
                                                        <Stack
                                                            direction={{
                                                                xs: "column",
                                                                md: "row",
                                                            }}
                                                            spacing={1}
                                                            alignItems={{
                                                                xs: "flex-start",
                                                                md: "center",
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="h5"
                                                                sx={{
                                                                    fontWeight: 900,
                                                                    lineHeight: 1.15,
                                                                color: KDS_COLORS.text,
                                                                textTransform: "uppercase",
                                                                }}
                                                            >
                                                                {label}
                                                            </Typography>
                                                            {!isSubstationRouteActive &&
                                                            Number(section.substation) > 0 ? (
                                                                <Button
                                                                    variant="outlined"
                                                                    onClick={() =>
                                                                        handleOpenSubstation(
                                                                            section.substation
                                                                        )
                                                                    }
                                                                    sx={{
                                                                        minHeight: 44,
                                                                        px: 2,
                                                                        fontSize: "0.95rem",
                                                                        fontWeight: 700,
                                                                        color: KDS_COLORS.text,
                                                                        borderColor: KDS_COLORS.border,
                                                                        backgroundColor:
                                                                            KDS_COLORS.panelSoft,
                                                                    }}
                                                                >
                                                                    {t(
                                                                        "kdsStations.openSubstation"
                                                                    )}
                                                                </Button>
                                                            ) : null}
                                                        </Stack>
                                                        <Typography
                                                            variant="body1"
                                                            color="text.secondary"
                                                            sx={{
                                                                mt: 0.5,
                                                                fontSize: {
                                                                    xs: "1rem",
                                                                    md: "1.05rem",
                                                                },
                                                                color: KDS_COLORS.muted,
                                                            }}
                                                        >
                                                            {t("kdsStations.itemsCount", {
                                                                count: section.groups.length,
                                                            })}
                                                        </Typography>
                                                    </Box>
                                                    <Button
                                                        variant="contained"
                                                        onClick={() =>
                                                            handleFinish(
                                                                Number(section.substation) === 0
                                                                    ? undefined
                                                                    : section.substation
                                                            )
                                                        }
                                                        disabled={
                                                            finishMutation.isLoading ||
                                                            section.groups.length === 0 ||
                                                            Number(section.substation) === 0
                                                        }
                                                        sx={{
                                                            minHeight: 60,
                                                            px: 3,
                                                            fontSize: "1.05rem",
                                                            fontWeight: 800,
                                                            backgroundColor: KDS_COLORS.accent,
                                                        }}
                                                    >
                                                        {activeFinishKey === sectionKey
                                                            ? t("kdsStations.finishing")
                                                            : t(
                                                                  "kdsStations.finishCurrentItems"
                                                              )}
                                                    </Button>
                                                </Stack>

                                                {section.groups.length > 0 ? (
                                                    <Stack
                                                        spacing={2}
                                                        sx={{ flex: 1, justifyContent: "flex-start" }}
                                                    >
                                                        {section.groups.map((group, index) => (
                                                            <StationGroupCard
                                                                key={`${
                                                                    group?.similarity_signature ||
                                                                    "group"
                                                                }-${section.substation}-${index}`}
                                                                group={group}
                                                                itemLabelsMap={itemLabelsMap}
                                                                t={t}
                                                                distanceLocale={distanceLocale}
                                                                layout={
                                                                    isSubstationRouteActive
                                                                        ? "lane"
                                                                        : "board"
                                                                }
                                                            />
                                                        ))}
                                                    </Stack>
                                                ) : (
                                                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                                                        {t("kdsStations.noCurrentItems")}
                                                    </Alert>
                                                )}
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </Box>
                    ) : null}
                </Stack>
            </Box>
        </>
    );
}

export default KdsStationsPage;
